
import { computed, defineComponent } from "vue";
import BoardView from "@/components/primitive/BoardView.vue";
import {
  InitialPositionType,
  Move,
  PositionChange,
  RecordMetadataKey,
} from "@/shogi";
import { RectSize } from "@/components/primitive/Types";
import { Action, Mutation, useStore } from "@/store";
import ButtonIcon from "@/components/primitive/ButtonIcon.vue";
import { Mode } from "@/store/mode";

export default defineComponent({
  name: "BoardPane",
  components: {
    BoardView,
    ButtonIcon,
  },
  props: {
    maxSize: {
      type: RectSize,
      required: true,
    },
  },
  setup() {
    const store = useStore();

    const onMove = (move: Move) => {
      store.dispatch(Action.DO_MOVE_BY_USER, move);
    };

    const onEdit = (change: PositionChange) => {
      store.commit(Mutation.EDIT_POSITION, change);
    };

    const onGame = () => {
      store.commit(Mutation.SHOW_GAME_DIALOG);
    };

    const onStop = () => {
      store.dispatch(Action.STOP_GAME);
    };

    const onResign = () => {
      store.dispatch(Action.RESIGN_BY_USER);
    };

    const onResearch = () => {
      store.commit(Mutation.SHOW_RESEARCH_DIALOG);
    };

    const onEndResearch = () => {
      store.dispatch(Action.STOP_RESEARCH);
    };

    const onStartEditPosition = () => {
      store.dispatch(Action.START_POSITION_EDITING);
    };

    const onEndEditPosition = () => {
      store.dispatch(Action.END_POSITION_EDITING);
    };

    const onInitPositionStandard = () => {
      store.dispatch(Action.INITIALIZE_POSITION, InitialPositionType.STANDARD);
    };

    const onInitPositionTsumeShogi = () => {
      store.dispatch(
        Action.INITIALIZE_POSITION,
        InitialPositionType.TSUME_SHOGI
      );
    };

    const onChangeTurn = () => {
      store.commit(Mutation.CHANGE_TURN);
    };

    const onOpenAppSettings = () => {
      store.commit(Mutation.OPEN_APP_SETTING_DIALOG);
    };

    const onOpenEngineSettings = () => {
      store.commit(Mutation.OPEN_USI_ENGINE_MANAGEMENT_DIALOG);
    };

    const onFlip = () => {
      store.commit(Mutation.FLIP_BOARD);
    };

    const onPaste = () => {
      store.commit(Mutation.SHOW_PASTE_DIALOG);
    };

    const onCopy = () => {
      store.dispatch(Action.COPY_RECORD);
    };

    const onRemoveAfter = () => {
      store.dispatch(Action.REMOVE_RECORD_AFTER);
    };

    const allowEdit = computed(
      () => store.state.mode === Mode.POSITION_EDITING
    );

    const allowMove = computed(() => store.getters.isMovableByUser);

    const position = computed(() => store.state.record.position);

    const lastMove = computed(() => {
      const move = store.state.record.current.move;
      return move instanceof Move ? move : null;
    });

    const pieceImageType = computed(() => store.state.appSetting.pieceImage);

    const boardImageType = computed(() => store.state.appSetting.boardImage);

    const flip = computed(() => store.state.appSetting.boardFlipping);

    const blackPlayerName = computed(() => {
      return store.state.record.metadata.getStandardMetadata(
        RecordMetadataKey.BLACK_NAME
      );
    });

    const whitePlayerName = computed(() => {
      return store.state.record.metadata.getStandardMetadata(
        RecordMetadataKey.WHITE_NAME
      );
    });

    const blackPlayerTimeMs = computed(() =>
      store.state.mode === Mode.GAME ? store.state.game.blackTimeMs : undefined
    );
    const blackPlayerByoyomi = computed(() =>
      store.state.mode === Mode.GAME ? store.state.game.blackByoyomi : undefined
    );
    const whitePlayerTimeMs = computed(() =>
      store.state.mode === Mode.GAME ? store.state.game.whiteTimeMs : undefined
    );
    const whitePlayerByoyomi = computed(() =>
      store.state.mode === Mode.GAME ? store.state.game.whiteByoyomi : undefined
    );

    const controlStates = computed(() => {
      return {
        game: store.state.mode === Mode.NORMAL,
        stop: store.state.mode === Mode.GAME,
        resign: store.state.mode === Mode.GAME && store.getters.isMovableByUser,
        research: store.state.mode === Mode.NORMAL,
        endResearch: store.state.mode === Mode.RESEARCH,
        startEditPosition: store.state.mode === Mode.NORMAL,
        endEditPosition: store.state.mode === Mode.POSITION_EDITING,
        initPosition: store.state.mode === Mode.POSITION_EDITING,
        removeAfter:
          store.state.mode === Mode.NORMAL ||
          store.state.mode === Mode.RESEARCH,
        paste: store.state.mode === Mode.NORMAL,
        appSettings: store.state.mode === Mode.NORMAL,
        engineSettings: store.state.mode === Mode.NORMAL,
      };
    });

    return {
      pieceImageType,
      boardImageType,
      position,
      lastMove,
      flip,
      blackPlayerName,
      whitePlayerName,
      blackPlayerTimeMs,
      blackPlayerByoyomi,
      whitePlayerTimeMs,
      whitePlayerByoyomi,
      controlStates,
      onMove,
      onEdit,
      onGame,
      onStop,
      onResign,
      onResearch,
      onEndResearch,
      onStartEditPosition,
      onEndEditPosition,
      onInitPositionStandard,
      onInitPositionTsumeShogi,
      onChangeTurn,
      onOpenAppSettings,
      onOpenEngineSettings,
      onFlip,
      onPaste,
      onCopy,
      onRemoveAfter,
      allowEdit,
      allowMove,
    };
  },
});
